import React, { useEffect, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { AuthenticationStatus } from "@/models/enum";
import { IAuthorizationProviderProps } from "../models/IAuth.interface";
import { eeEnvVariables } from "@/utilities/constants";
import { axiosGetIDPConfig } from "@/modules/hiring/stateManagement/api";
import { useKeycloak } from "../hooks/useKeycloack";

  export const TeamsAppAuthProvider = ({
    setAuthStatus,
    authStatus,
    setErrorMessage,
    isWindowSizeUnsupported,
  }: IAuthorizationProviderProps) => {
  const [loading, setLoading] = useState(true);
  const { fetchInitialToken, getAccessToken, manualRefresh } = useKeycloak();

  useEffect(() => {
    let mounted = true;
    let hasInitialized = false;

    const initTeamsAndAuth = async () => {
      // Prevent multiple initializations
      if (hasInitialized) {
        console.log("Teams already initialized, skipping...");
        return;
      }

      try {
        await microsoftTeams.app.initialize();
        hasInitialized = true;

        // Notify readiness (optional)
        microsoftTeams.app.notifyAppLoaded();
        microsoftTeams.app.notifySuccess();

        // Get SSO token or context you already use:
        microsoftTeams.authentication.getAuthToken({
          successCallback: async (ssoToken: string) => {
            if (!mounted) return;
            // Call BE to exchange SSO token for Keycloak token
            try {
             const tokenResponse = await fetchInitialToken(ssoToken);
             if (tokenResponse) {
              console.log("Teams app auth success");
              setAuthStatus({
                status: AuthenticationStatus.Succeeded,
                token: tokenResponse.access_token,
              });
             }
              // Now your hook will auto schedule refresh 5 minutes before expiry
            } catch (err) {
              console.error("Failed to get initial Keycloak token:", err);
            }
          },
          failureCallback: (err: any) => {
            console.error("Teams auth failed", err);
          },
          resources: [`api://${eeEnvVariables.RESOURCE_URI}/${eeEnvVariables.AUTH_CLIENT_ID}`],
        });

        // when Teams resumes (app brought to foreground), attempt a manual refresh
        // Note: registerOnResumeHandler exists in newer SDKs; if not present use visibilitychange fallback
        if ((microsoftTeams.app as any).registerOnResumeHandler) {
          (microsoftTeams.app as any).registerOnResumeHandler(async () => {
            console.log("Teams app resumed manualRefresh");
            // call manual refresh — this updates the token if refresh token still valid
            await manualRefresh();
          });
        } else {
          // fallback: refresh when page becomes visible
          const onVisibility = async () => {
            if (!document.hidden) await manualRefresh();
          };
          document.addEventListener("visibilitychange", onVisibility);
        }
      } catch (err) {
        console.error("Teams init error:", err);
      }
    };

    initTeamsAndAuth();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  return  (
      <>
        {(authStatus.status === AuthenticationStatus.Unauthenticated ||
          authStatus.status === AuthenticationStatus.InProgress) &&
          !isWindowSizeUnsupported && <h1> loading {authStatus.status}</h1>}
      </>
    );
};
